import { useEffect, useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { AppLayout } from '../components/AppLayout'
import { useReveal } from '../lib/reveal'

/**
 * /about — company story, footprint, leadership, contact. Static
 * marketing content; no API calls. Same hero pattern as Landing
 * + Sell so the public surface feels like one continuous
 * experience.
 */
export function About() {
  return (
    <AppLayout>
      <Hero />
      <Story />
      <AccessComparison />
      <Footprint />
      <Contact />
    </AppLayout>
  )
}

/** Count up to a target once it scrolls into view (respects reduced motion). */
function useCountOnView(target: number, ref: React.RefObject<HTMLElement | null>, durationMs = 1500): number {
  const inView = useInView(ref, { once: true, margin: '-80px' })
  const [v, setV] = useState(0)
  useEffect(() => {
    if (!inView) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setV(target); return }
    let raf = 0
    const t0 = performance.now()
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / durationMs)
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, target, durationMs])
  return v
}

function Dash() {
  return (
    <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ink/[0.06] text-soft" aria-hidden>
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
    </span>
  )
}
function Check() {
  return (
    <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold" aria-hidden>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    </span>
  )
}

/**
 * The value proposition, made visual: everyone else charges a toll just to
 * SEE the market and reach a supplier. We don't. An access cost ticks up on
 * the left; ours holds at $0. No competitor is named — the model is the point.
 */
function AccessComparison() {
  const ref = useRef<HTMLElement>(null)
  const cost = useCountOnView(1800, ref)
  return (
    <section ref={ref} className="mx-auto max-w-5xl px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-2xl"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">Access, not tolls</p>
        <h2 className="mt-3 font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight text-ink">
          You shouldn't have to pay to see the market.
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-ink/70">
          Most trade marketplaces gate the basics — a monthly membership just to see prices,
          and more to reach the people holding the stones. On DiaEx, browsing, real prices,
          and a direct line to verified suppliers are free. Always. We only charge a flat
          service fee when you ask us to do the work.
        </p>
      </motion.div>

      <div className="mt-12 grid items-stretch gap-5 md:grid-cols-2">
        {/* The usual way — cost of access ticks up */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col rounded-2xl border border-line bg-paper p-8"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-soft">The usual way</p>
          <ul className="mt-6 space-y-3.5 text-[14px] text-ink/70">
            <li className="flex items-start gap-2.5"><Dash /> Membership just to see prices</li>
            <li className="flex items-start gap-2.5"><Dash /> Pay per lead to contact a supplier</li>
            <li className="flex items-start gap-2.5"><Dash /> Inventory locked without a plan</li>
            <li className="flex items-start gap-2.5"><Dash /> Markup hidden inside the price</li>
          </ul>
          <div className="mt-auto border-t border-line pt-6">
            <p className="font-serif text-4xl leading-none text-ink/80 tabular">
              ~${cost.toLocaleString('en-US')}<span className="font-sans text-base text-soft">/yr</span>
            </p>
            <p className="mt-2 text-[12px] text-soft">a typical cost of access — before a single stone</p>
          </div>
        </motion.div>

        {/* On DiaEx — holds at $0 */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ delay: 0.12, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-col overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-cream to-paper p-8 shadow-[0_28px_64px_-32px_rgba(184,146,58,0.45)] before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-gold/60 before:to-transparent before:content-['']"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">On DiaEx</p>
          <ul className="mt-6 space-y-3.5 text-[14px] text-ink">
            <li className="flex items-start gap-2.5"><Check /> Browse live inventory</li>
            <li className="flex items-start gap-2.5"><Check /> See real prices, once verified</li>
            <li className="flex items-start gap-2.5"><Check /> Contact verified suppliers directly</li>
            <li className="flex items-start gap-2.5"><Check /> No membership to enter the market</li>
          </ul>
          <div className="mt-auto border-t border-gold/20 pt-6">
            <p className="font-serif text-6xl leading-none text-gold tabular">
              $0<span className="font-sans text-base text-ink/45"> / forever</span>
            </p>
            <p className="mt-2 text-[12px] text-ink/60">flat service fee only when you hand us the work</p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

function Hero() {
  return (
    <section className="mx-auto max-w-5xl px-6 pt-20 pb-12 text-center">
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xs font-semibold uppercase tracking-[0.18em] text-primary"
      >
        About DiaEx
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-5 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-ink"
      >
        The diamond exchange
        <br />
        <span className="wordmark-glow">built by twenty years in the trade.</span>
      </motion.h1>
    </section>
  )
}

function Story() {
  const ref = useReveal<HTMLDivElement>()
  return (
    <section className="mx-auto max-w-3xl px-6 py-12">
      <div ref={ref} className="reveal space-y-6 text-[15px] leading-[1.7] text-ink/85">
        <p>
          DiaEx is a diamond exchange — an Aska Jewels product. Aska has been in the
          diamond business since 2006, first in Mumbai, where it built relationships with
          the cutters and traders who supply most of the world's stones, and since 2019
          in New York. DiaEx turns that supplier network into an open marketplace:
          browse live verified inventory and deal with suppliers directly, buy through
          Aska Services — QC and insured shipping handled for you — or hand the hunt to
          Diaex Sourcing: name the stone and the most you'll pay, and we watch the live
          market and move the moment it surfaces at your number.
        </p>
        <p>
          The exchange specializes in lab-grown diamonds — HPHT and CVD, certified by IGI
          and GIA. Every stone listed has a real cert, media, and a transparent price.
          No mystery margins, no bait-and-switch, no "we'll find one for you" stalling tactics.
        </p>
        <p>
          We're deliberate about how we make money, because the trade has had enough of
          hidden spreads. Browsing and dealing direct is free — the price you see is the
          supplier's real ask, and we take no commission on it, ever. When we do the work,
          you pay a flat service fee, never a percentage of the stone; return protection
          is optional and priced in the open; and Aska+ membership makes the sourcing
          desk free. A broker hides the spread. We show it.
        </p>
        <p>
          The platform you're on now is the front door we wish we'd had ten years ago: chat
          with us about what you're looking for, browse a verified inventory in seconds, place
          orders without filling out twelve-field PDFs. The same diamonds we've been moving
          through email and WhatsApp for two decades — just faster.
        </p>
      </div>
    </section>
  )
}

function Footprint() {
  return (
    <section className="mx-auto max-w-3xl px-6 py-12">
      <div className="card text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-soft mb-3">Where we operate</p>
        <p className="text-base text-ink leading-relaxed">
          Mumbai (sourcing + cutting) and New York (sales + fulfillment).
          IGI- and GIA-certified inventory only. Worldwide shipping via insured FedEx.
        </p>
      </div>
    </section>
  )
}

function Contact() {
  return (
    <section className="mx-auto max-w-3xl px-6 py-20 text-center">
      <h2 className="text-3xl font-bold text-ink tracking-tight">Talk to us</h2>
      <p className="mt-4 text-sm text-muted">
        Email <a href="mailto:info@thediaex.com" className="text-primary font-medium hover:underline">info@thediaex.com</a>{' '}
        — we read every message and reply within one business day.
      </p>
      <div className="mt-10 flex justify-center gap-2 text-xs text-soft">
        <span>Mumbai · New York</span>
        <span>·</span>
        <span>Worldwide shipping</span>
      </div>
      <p className="mt-6 text-xs text-soft">DiaEx — an Aska product</p>
    </section>
  )
}
